import type { DataTableFilterMeta, DataTableFilterMetaData } from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';

export interface TableViewState {
    filters: DataTableFilterMeta;
    filterValueTypes?: Record<string, 'number' | 'boolean'>;
    search: string;
    first: number;
    rows: number;
    sortField: string;
    sortOrder: number;
}

const MATCH_MODES = new Set<string>(Object.values(FilterMatchMode));
const VIEW_PARAMS = ['search', 'page', 'rows', 'sort', 'order'] as const;

type MatchMode = NonNullable<DataTableFilterMetaData['matchMode']>;

/** Проверяет режим сравнения фильтра */
const isMatchMode = (value: unknown): value is MatchMode =>
    typeof value === 'string' && MATCH_MODES.has(value);

/** Проверяет, являются ли настройки таблицы объектом */
const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/** Проверяет допустимое значение фильтра */
const isFilterValue = (value: unknown): boolean => {
    if (value === null || typeof value === 'boolean') return true;

    if (typeof value === 'number') return Number.isFinite(value);

    if (typeof value === 'string') return value.length <= 256;

    if (!Array.isArray(value) || value.length > 50) return false;

    return value.every((item) =>
        typeof item === 'boolean'
        || (typeof item === 'number' && Number.isFinite(item))
        || (typeof item === 'string' && item.length <= 256));
};

/** Определяет заполненное значение фильтра */
const hasValue = (value: unknown): boolean =>
    value !== null && value !== undefined && value !== ''
    && (!Array.isArray(value) || value.length > 0);

/** Читает условие поиска таблицы */
const readFilter = (value: unknown): DataTableFilterMeta[string] | null => {
    if (!isRecord(value)) return null;

    if (Array.isArray(value.constraints)
        && (value.operator === 'and' || value.operator === 'or')) {
        const constraints = value.constraints
            .filter((item): item is Record<string, unknown> =>
                isRecord(item) && isFilterValue(item.value)
                && typeof item.matchMode === 'string' && MATCH_MODES.has(item.matchMode))
            .map((item) => ({
                value: item.value,
                matchMode: item.matchMode
            }));

        return constraints.length > 0
            ? {
                operator: value.operator,
                constraints
            } as DataTableFilterMeta[string]
            : null;
    }

    return isFilterValue(value.value)
        && typeof value.matchMode === 'string' && MATCH_MODES.has(value.matchMode)
        ? {
            value: value.value,
            matchMode: value.matchMode
        } as DataTableFilterMeta[string]
        : null;
};

/** Определяет активное условие поиска */
const isActiveFilter = (filter: DataTableFilterMeta[string]): boolean =>
    'constraints' in filter
        ? filter.constraints.some((constraint) => hasValue(constraint.value))
        : hasValue(filter.value);

/** Восстанавливает состояние таблицы из проверенного параметра URL */
export const decodeTableViewState = (
    raw: string | null,
    defaults: TableViewState
): TableViewState => {
    const filters: DataTableFilterMeta = Object.fromEntries(
        Object.entries(defaults.filters).map(([key, value]) => [key, readFilter(value) ?? value]),
    );

    const fallback = {
        ...defaults,
        filters
    };

    if (!raw || raw.length > 16_000) return fallback;

    try {
        const parsed: unknown = JSON.parse(raw);

        if (!isRecord(parsed)) return fallback;

        if (isRecord(parsed.f)) {
            for (const key of Object.keys(defaults.filters)) {
                const filter = readFilter(parsed.f[key]);

                if (filter && isActiveFilter(filter)) filters[key] = filter;
            }
        }

        const search = typeof parsed.q === 'string' && parsed.q.length <= 256
            ? parsed.q
            : defaults.search;

        const first = Number.isInteger(parsed.p) && Number(parsed.p) >= 0 && Number(parsed.p) <= 1_000_000
            ? Number(parsed.p)
            : defaults.first;

        const rows = Number.isInteger(parsed.r) && Number(parsed.r) >= 1 && Number(parsed.r) <= 1000
            ? Number(parsed.r)
            : defaults.rows;

        const sortField = typeof parsed.sf === 'string' && parsed.sf.length <= 80
            ? parsed.sf
            : defaults.sortField;

        const sortOrder = parsed.so === -1 || parsed.so === 0 || parsed.so === 1
            ? parsed.so
            : defaults.sortOrder;

        if ('global' in filters && 'value' in filters.global) {
            filters.global = {
                ...filters.global,
                value: search || null
            };
        }

        return {
            filters,
            search,
            first,
            rows,
            sortField,
            sortOrder
        };
    } catch {
        return fallback;
    }
};

/** Кодирует активные фильтры и параметры просмотра для ссылки */
export const encodeTableViewState = (
    state: TableViewState,
    defaults: TableViewState
): string | null => {
    const filters: DataTableFilterMeta = {};

    for (const key of Object.keys(defaults.filters)) {
        const filter = readFilter(state.filters[key]);

        if (filter && isActiveFilter(filter) && key !== 'global') filters[key] = filter;
    }

    const payload: Record<string, unknown> = {};

    if (Object.keys(filters).length > 0) payload.f = filters;
    if (state.search !== defaults.search) payload.q = state.search.slice(0, 256);
    if (state.first !== defaults.first) payload.p = state.first;
    if (state.rows !== defaults.rows) payload.r = state.rows;
    if (state.sortField !== defaults.sortField) payload.sf = state.sortField;
    if (state.sortOrder !== defaults.sortOrder) payload.so = state.sortOrder;

    return Object.keys(payload).length > 0
        ? JSON.stringify(payload)
        : null;
};

/** Читает значение фильтра из текста */
const parseFilterValue = (
    raw: string,
    type: 'number' | 'boolean' | undefined
): string | number | boolean | null => {
    if (raw.length > 256) return null;

    if (type === 'boolean') {
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        return null;
    }

    if (type === 'number') {
        if (!/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(raw)) return null;

        const value = Number(raw);

        return Number.isFinite(value)
            ? value
            : null;
    }

    return raw;
};

/** Проверяет, содержит ли ссылка параметры просмотра текущей таблицы */
export const hasTableViewStateParams = (
    params: URLSearchParams,
    defaults: TableViewState
): boolean => {
    if (params.has('table') || VIEW_PARAMS.some((key) => params.has(key))) return true;

    return Object.keys(defaults.filters)
        .some((key) =>
            params.has(key) || params.has(`${key}.matchMode`) || params.has(`${key}.operator`));
};

/** Удаляет параметры просмотра таблицы, сохраняя остальные параметры ссылки */
export const clearTableViewStateParams = (
    params: URLSearchParams,
    defaults: TableViewState
): void => {
    params.delete('table');

    for (const key of VIEW_PARAMS) params.delete(key);

    for (const key of Object.keys(defaults.filters)) {
        params.delete(key);
        params.delete(`${key}.matchMode`);
        params.delete(`${key}.operator`);
    }
};

/** Восстанавливает фильтры и параметры просмотра из читаемой ссылки */
export const decodeTableViewStateParams = (
    params: URLSearchParams,
    defaults: TableViewState
): TableViewState => {
    const filters: DataTableFilterMeta = Object.fromEntries(
        Object.entries(defaults.filters).map(([key, value]) => [key, readFilter(value) ?? value]),
    );

    for (const [key, defaultFilter] of Object.entries(filters)) {
        if (key === 'global') continue;

        const values = params.getAll(key)
            .slice(0, 50)
            .map((raw) => parseFilterValue(raw, defaults.filterValueTypes?.[key]))
            .filter((value) => value !== null && value !== '');

        if (values.length === 0) continue;

        const defaultMode = 'matchMode' in defaultFilter
            ? defaultFilter.matchMode ?? FilterMatchMode.CONTAINS
            : FilterMatchMode.CONTAINS;

        const modes = params.getAll(`${key}.matchMode`);

        const operator = params.get(`${key}.operator`);

        if (operator === 'and' || operator === 'or') {
            filters[key] = {
                operator,
                constraints: values.map((value, index) => ({
                    value,
                    matchMode: isMatchMode(modes[index])
                        ? modes[index]
                        : defaultMode,
                })),
            };
        } else {
            const matchMode = isMatchMode(modes[0])
                ? modes[0]
                : defaultMode;

            filters[key] = {
                value: matchMode === FilterMatchMode.IN || matchMode === FilterMatchMode.NOT_IN || values.length > 1
                    ? values
                    : values[0],
                matchMode,
            };
        }
    }

    const search = params.get('search') ?? defaults.search;

    const rowsParam = Number(params.get('rows'));

    const rows = Number.isInteger(rowsParam) && rowsParam >= 1 && rowsParam <= 1000
        ? rowsParam
        : defaults.rows;

    const pageParam = Number(params.get('page'));

    const requestedFirst = (pageParam - 1) * rows;

    const first = Number.isInteger(pageParam) && pageParam >= 1 && requestedFirst <= 1_000_000
        ? requestedFirst
        : defaults.first;

    const sortField = params.get('sort') ?? defaults.sortField;

    const orderParam = params.get('order');

    let sortOrder = defaults.sortOrder;

    if (orderParam === 'asc') sortOrder = 1;
    if (orderParam === 'desc') sortOrder = -1;
    if (orderParam === 'none') sortOrder = 0;

    if ('global' in filters && 'value' in filters.global) {
        filters.global = {
            ...filters.global,
            value: search || null
        };
    }

    return {
        ...defaults,
        filters,
        search: search.slice(0, 256),
        first,
        rows,
        sortField: sortField.slice(0, 80),
        sortOrder,
    };
};

/** Добавляет активное состояние таблицы в читаемые параметры ссылки */
export const encodeTableViewStateParams = (
    params: URLSearchParams,
    state: TableViewState,
    defaults: TableViewState,
): void => {
    clearTableViewStateParams(params, defaults);

    for (const [key, defaultFilter] of Object.entries(defaults.filters)) {
        if (key === 'global') continue;

        const filter = readFilter(state.filters[key]);

        if (!filter || !isActiveFilter(filter)) continue;

        const defaultMode = 'matchMode' in defaultFilter
            ? defaultFilter.matchMode ?? FilterMatchMode.CONTAINS
            : FilterMatchMode.CONTAINS;

        if ('constraints' in filter) {
            params.set(`${key}.operator`, filter.operator);

            for (const constraint of filter.constraints) {
                if (!hasValue(constraint.value)) continue;

                params.append(key, String(constraint.value));
                params.append(`${key}.matchMode`, constraint.matchMode ?? defaultMode);
            }
        } else {
            const values = Array.isArray(filter.value)
                ? filter.value
                : [filter.value];

            for (const value of values) params.append(key, String(value));

            if (filter.matchMode !== defaultMode) {
                params.set(`${key}.matchMode`, filter.matchMode ?? defaultMode);
            }
        }
    }

    if (state.search !== defaults.search) params.set('search', state.search.slice(0, 256));
    if (state.rows !== defaults.rows) params.set('rows', String(state.rows));
    if (state.first !== defaults.first) params.set('page', String(Math.floor(state.first / state.rows) + 1));
    if (state.sortField !== defaults.sortField) params.set('sort', state.sortField);
    if (state.sortOrder !== defaults.sortOrder) {
        params.set('order', state.sortOrder === 1
            ? 'asc'
            : state.sortOrder === -1
                ? 'desc'
                : 'none');
    }
};
